// src/pages/Pricing/PricingPage.tsx
import React, { useState, useEffect } from "react";
import { CilpronApi } from "../../../../constant/Banner";
import type { Product, PlanFeature, Price, Plan, BillingCycle } from "../../../../types/billingTypes";

const { BASE_URL, TOKEN_KEY } = CilpronApi;

const PRODUCT_META: Record<string, { icon: string; color: string; gradient: string }> = {
  "Hospitality Software":       { icon: "🏨", color: "#92400E", gradient: "linear-gradient(135deg,#92400E,#D97706)" },
  "CRM Software":               { icon: "🤝", color: "#1E40AF", gradient: "linear-gradient(135deg,#1E40AF,#2563EB)" },
  "Email Services":             { icon: "✉️",  color: "#065F46", gradient: "linear-gradient(135deg,#065F46,#059669)" },
  "Swift Grid":                 { icon: "⚡",  color: "#5B21B6", gradient: "linear-gradient(135deg,#5B21B6,#7C3AED)" },
  "Accounting":                 { icon: "📊",  color: "#0E7490", gradient: "linear-gradient(135deg,#0E7490,#0891B2)" },
  "Chat Channel":               { icon: "💬",  color: "#9D174D", gradient: "linear-gradient(135deg,#9D174D,#DB2777)" },
  "Call App":                   { icon: "📞",  color: "#1E3A5F", gradient: "linear-gradient(135deg,#1E3A5F,#1D4ED8)" },
  "Agentic Customer Services":  { icon: "🤖",  color: "#78350F", gradient: "linear-gradient(135deg,#78350F,#B45309)" },
  "API Access (Pay-As-You-Go)": { icon: "🔌",  color: "#1E40AF", gradient: "linear-gradient(135deg,#1E3A8A,#1E40AF)" },
  "Main SaaS Platform":         { icon: "🏢",  color: "#1E293B", gradient: "linear-gradient(135deg,#1E293B,#334155)" },
  "White Label Add-on":         { icon: "🎨",  color: "#4C1D95", gradient: "linear-gradient(135deg,#4C1D95,#6D28D9)" },
};
const DEFAULT_META = { icon: "📦", color: "#292929", gradient: "linear-gradient(135deg,#292929,#525252)" };

// A plan is "free" only when it is flat-priced at $0.
// Metered/per_seat plans with amount=0 are still paid (pay-per-use).
function isPlanFree(price: Price): boolean {
  return price.pricing_model === "flat" && price.amount === 0;
}

function formatPrice(
  price: Price,
  cycle: BillingCycle
): { main: string; period: string; note?: string; modelLabel?: string } {
  if (price.pricing_model === "metered") {
    const u = (price.unit_amount ?? 0) / 100;
    const label = price.unit_label ?? "unit";
    return {
      main:       u === 0 ? "$0" : `$${u < 0.01 ? u.toFixed(4) : u.toFixed(2)}`,
      period:     `per ${label}`,
      note:       price.free_units > 0 ? `First ${price.free_units.toLocaleString()} ${label}s free` : undefined,
      modelLabel: "metered",
    };
  }
  if (price.pricing_model === "per_seat") {
    const dollars = (price.unit_amount ?? 0) / 100;
    return {
      main:       dollars === 0 ? "Free" : `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`,
      period:     "per seat / mo",
      modelLabel: "per seat",
    };
  }
  // flat
  if (price.amount === 0) return { main: "Free", period: "forever" };
  const monthly = cycle === "yearly" ? price.amount / 12 : price.amount;
  const dollars = monthly / 100;
  return {
    main:   `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`,
    period: "/month",
    note:   cycle === "yearly" ? `Billed $${(price.amount / 100).toFixed(0)}/year` : undefined,
  };
}

function featureDisplay(value: string, displayLabel: string | null, valueType: string): React.ReactNode {
  if (valueType === "boolean") {
    return value === "true"
      ? <span style={{ color: "#059669", fontSize: 18 }}>✓</span>
      : <span style={{ color: "#d1d5db", fontSize: 18 }}>—</span>;
  }
  if (value === "unlimited") return <span style={{ color: "#059669", fontWeight: 700 }}>Unlimited</span>;
  return <span>{displayLabel ?? value}</span>;
}

function getFeatureRows(plans: Plan[]): { key: string; name: string }[] {
  const seen = new Map<string, string>();
  plans.forEach(plan =>
    (plan.features?.$values ?? []).forEach(f => {
      if (!seen.has(f.featureKey)) seen.set(f.featureKey, f.featureName);
    })
  );
  return Array.from(seen.entries()).map(([key, name]) => ({ key, name }));
}

function getPlanFeature(plan: Plan, key: string): PlanFeature | undefined {
  return (plan.features?.$values ?? []).find(f => f.featureKey === key);
}

interface PricingPageProps {
  product:      Product;
  onBack:       () => void;
  onSubscribe?: (plan: Plan, price: Price) => void;
}

const PricingPage: React.FC<PricingPageProps> = ({ product, onBack, onSubscribe }) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const meta  = PRODUCT_META[product.name] ?? DEFAULT_META;

  const [plans, setPlans]       = useState<Plan[]>([]);
  const [priceMap, setPriceMap] = useState<Record<string, Price>>({});
  const [loading, setLoading]   = useState(true);
  const [cycle, setCycle]       = useState<BillingCycle>("monthly");

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetch(`${BASE_URL}/Plans/public`, { headers: { Authorization: `Bearer ${token}`, accept: "*/*" } }).then(r => r.json()),
      fetch(`${BASE_URL}/Prices`,       { headers: { Authorization: `Bearer ${token}`, accept: "*/*" } }).then(r => r.json()),
    ])
      .then(([planJson, priceJson]) => {
        const allPlans: Plan[] = planJson?.data?.$values ?? [];
        setPlans(allPlans.filter(p => p.productId === product.id && p.isActive && p.isPublic).sort((a, b) => a.sortOrder - b.sortOrder));
        const allPrices: Price[] = priceJson?.data?.$values ?? [];
        const map: Record<string, Price> = {};
        allPrices.forEach(p => { if (!map[p.plan_id] || p.currency === "USD") map[p.plan_id] = p; });
        setPriceMap(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, product.id]);

  const visiblePlans = plans.filter(p => {
    if (cycle === "lifetime") return p.billingInterval === "lifetime";
    if (cycle === "yearly")   return p.billingInterval === "yearly" || p.billingInterval === "monthly";
    return p.billingInterval === "monthly";
  });

  const cycleOrder: BillingCycle[]  = ["monthly", "yearly", "lifetime"];
  const intervals                   = Array.from(new Set(plans.map(p => p.billingInterval as BillingCycle)));
  const availableIntervals          = cycleOrder.filter(c => intervals.includes(c));
  const featureRows                 = getFeatureRows(visiblePlans);
  const popularIdx                  = visiblePlans.length > 2 ? 1 : -1;
  const getPlanPrice                = (plan: Plan): Price | undefined => priceMap[plan.id];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .pp-root *, .pp-root *::before, .pp-root *::after {
          box-sizing: border-box;
          font-family: 'Inter', 'Segoe UI', sans-serif;
        }
        .pp-root { background: #f8fafc; color: #0f172a; }

        /* Hero */
        .pp-hero { background: ${meta.gradient}; position: relative; overflow: hidden; }
        .pp-hero::after { content:''; position:absolute; inset:0; background:rgba(0,0,0,.18); pointer-events:none; }
        .pp-hero-inner { position:relative; z-index:1; max-width:1100px; margin:0 auto; padding:40px 32px 48px; }
        .pp-back-btn {
          display:inline-flex; align-items:center; gap:6px;
          background:rgba(255,255,255,.15); border:1px solid rgba(255,255,255,.3);
          color:#fff; font-size:13px; font-weight:600; padding:8px 16px;
          border-radius:20px; cursor:pointer; margin-bottom:28px;
          transition:background .15s; backdrop-filter:blur(4px); line-height:1;
        }
        .pp-back-btn:hover { background:rgba(255,255,255,.28); }
        .pp-hero-icon  { font-size:48px; margin-bottom:12px; display:block; line-height:1; }
        .pp-hero-title { font-size:36px; font-weight:900; color:#fff; margin:0 0 8px; letter-spacing:-.5px; line-height:1.1; }
        .pp-hero-desc  { font-size:16px; color:rgba(255,255,255,.82); margin:0; max-width:520px; line-height:1.6; }

        /* Cycle toggle */
        .pp-controls { max-width:1100px; margin:0 auto; padding:28px 32px 0; display:flex; justify-content:center; }
        .pp-cycle-toggle { display:inline-flex; background:#e2e8f0; border-radius:10px; padding:4px; gap:3px; }
        .pp-cycle-btn {
          display:inline-flex; align-items:center; gap:6px;
          padding:8px 20px; border:none; border-radius:7px;
          font-size:13px; font-weight:600; cursor:pointer;
          background:transparent; color:#64748b; line-height:1; white-space:nowrap;
          transition:background .15s, color .15s, box-shadow .15s;
        }
        .pp-cycle-btn.active { background:#fff; color:#0f172a; box-shadow:0 1px 4px rgba(0,0,0,.12); }
        .pp-save-badge { font-size:10px; font-weight:700; background:#dcfce7; color:#166534; padding:2px 6px; border-radius:8px; text-transform:uppercase; letter-spacing:.3px; }

        /* Cards */
        .pp-cards-wrap { max-width:1100px; margin:0 auto; padding:32px 32px 0; }
        .pp-cards {
          display:grid;
          grid-template-columns:repeat(auto-fill, minmax(220px, 1fr));
          gap:20px;
          align-items:stretch;
        }

        .pp-plan-card {
          background:#fff; border:2px solid #e2e8f0; border-radius:16px;
          padding:24px 20px;
          display:flex; flex-direction:column;
          position:relative;
          transition:box-shadow .18s, border-color .18s, transform .15s;
        }
        .pp-plan-card.popular { border-color:${meta.color}; box-shadow:0 0 0 1px ${meta.color}33,0 8px 28px ${meta.color}28; }
        .pp-plan-card:hover   { box-shadow:0 8px 28px rgba(0,0,0,.1); transform:translateY(-2px); }

        .pp-popular-badge {
          position:absolute; top:-12px; left:50%; transform:translateX(-50%);
          background:${meta.color}; color:#fff; font-size:10px; font-weight:700;
          padding:3px 12px; border-radius:20px; white-space:nowrap;
          text-transform:uppercase; letter-spacing:.5px; line-height:1.5;
        }

        /* Card content spacing — every child gets its margin-bottom here, not gap */
        .pp-plan-name        { font-size:15px; font-weight:700; color:#0f172a; margin:0 0 16px; }
        .pp-price-block      { margin:0 0 16px; }
        .pp-plan-price-main  { font-size:38px; font-weight:900; color:#0f172a; letter-spacing:-1.5px; line-height:1; }
        .pp-plan-price-period{ font-size:13px; color:#64748b; font-weight:500; margin-top:4px; }
        .pp-plan-price-note  { font-size:11px; color:#94a3b8; margin-top:3px; }
        .pp-plan-price-model {
          display:inline-flex; align-items:center;
          font-size:10px; font-weight:700; color:#7c3aed; background:#ede9fe;
          padding:2px 7px; border-radius:4px; margin-top:6px;
          text-transform:uppercase; letter-spacing:.3px;
        }
        .pp-no-price  { font-size:13px; color:#94a3b8; font-style:italic; margin:0 0 16px; }

        .pp-trial-badge {
          display:inline-flex; align-items:center; gap:4px;
          font-size:11px; font-weight:700; color:#065f46; background:#d1fae5;
          padding:4px 10px; border-radius:6px; margin-bottom:12px; width:fit-content;
        }
        .pp-plan-desc {
          font-size:12px; color:#64748b; line-height:1.6; margin:0 0 14px;
        }
        .pp-plan-features-preview {
          display:flex; flex-direction:column; gap:8px;
          padding:14px 0 0; margin:0 0 14px;
          border-top:1px solid #f1f5f9;
        }
        .pp-feat-row   { display:flex; align-items:flex-start; gap:8px; font-size:12px; color:#374151; line-height:1.4; }
        .pp-feat-check { color:#059669; font-size:13px; flex-shrink:0; margin-top:1px; }

        /* CTA area — pinned to card bottom, buttons always same size */
        .pp-cta-wrap {
          margin-top:auto;
          padding-top:16px;
          display:flex; flex-direction:column; gap:8px;
        }

        /*
          Single button token: all card buttons share .pp-btn for shape/sizing.
          Colour variants: pp-btn-primary (filled), pp-btn-outline, pp-btn-ghost.
        */
        .pp-btn {
          display:flex; align-items:center; justify-content:center;
          width:100%;
          height:42px;          /* fixed — no padding variation */
          padding:0 16px;
          border-radius:10px;
          font-size:13px; font-weight:700; line-height:1; white-space:nowrap;
          cursor:pointer;
          transition:opacity .15s, transform .12s, background .15s, border-color .15s;
        }
        .pp-btn:active { transform:scale(.97); }

        .pp-btn-primary { background:${meta.color}; color:#fff; border:2px solid ${meta.color}; }
        .pp-btn-primary:hover { opacity:.88; }

        .pp-btn-outline { background:transparent; color:${meta.color}; border:2px solid ${meta.color}; }
        .pp-btn-outline:hover { background:${meta.color}12; }

        .pp-btn-ghost { background:transparent; color:#64748b; border:2px solid #e2e8f0; }
        .pp-btn-ghost:hover { border-color:#94a3b8; color:#374151; }

        /* Comparison table */
        .pp-compare { max-width:1100px; margin:40px auto 0; padding:0 32px 48px; }
        .pp-compare-title { font-size:20px; font-weight:800; color:#0f172a; margin:0 0 20px; }
        .pp-table {
          width:100%; border-collapse:collapse; background:#fff;
          border-radius:14px; overflow:hidden;
          box-shadow:0 1px 4px rgba(0,0,0,.06); border:1px solid #e2e8f0;
        }
        .pp-table th { background:#f8fafc; padding:14px 16px; font-size:12px; font-weight:700; color:#475569; text-align:left; border-bottom:1px solid #e2e8f0; }
        .pp-table th.plan-col { text-align:center; color:#0f172a; font-size:13px; background:#f1f5f9; }
        .pp-table th.popular-col { background:${meta.color}18; color:${meta.color}; }
        .pp-table td { padding:12px 16px; font-size:13px; color:#374151; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
        .pp-table td.plan-val { text-align:center; }
        .pp-table tr:last-child td { border-bottom:none; }
        .pp-table tr:hover td { background:#f8fafc; }

        /* Table buttons — same shape token, smaller */
        .pp-tbl-btn {
          display:inline-flex; align-items:center; justify-content:center;
          width:100%; height:34px; padding:0 12px;
          border-radius:8px; font-size:11px; font-weight:700;
          cursor:pointer; border:none; line-height:1;
          transition:opacity .15s;
        }
        .pp-tbl-btn:hover { opacity:.85; }
        .pp-tbl-btn-filled  { background:${meta.color}; color:#fff; }
        .pp-tbl-btn-outline { background:transparent; color:${meta.color}; border:2px solid ${meta.color}; }

        /* Footer */
        .pp-footer-note {
          max-width:1100px; margin:0 auto; padding:20px 32px 40px;
          display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px;
        }
        .pp-footer-note p { font-size:13px; color:#64748b; margin:0; }
        .pp-contact-link { font-size:13px; color:${meta.color}; font-weight:600; cursor:pointer; border:none; background:none; padding:0; }
        .pp-contact-link:hover { text-decoration:underline; }

        /* Loading / empty */
        .pp-loading { display:flex; align-items:center; justify-content:center; padding:80px 32px; gap:12px; color:#64748b; font-size:14px; }
        .pp-spinner { width:20px; height:20px; border:2px solid #e2e8f0; border-top-color:${meta.color}; border-radius:50%; animation:ppSpin .7s linear infinite; flex-shrink:0; }
        @keyframes ppSpin { to { transform:rotate(360deg); } }
        .pp-empty { text-align:center; padding:60px 32px; color:#64748b; font-size:14px; }

        @media (max-width:640px) {
          .pp-hero-inner { padding:28px 20px 36px; }
          .pp-hero-title { font-size:26px; }
          .pp-cards-wrap, .pp-controls { padding-left:16px; padding-right:16px; }
          .pp-cards { grid-template-columns:1fr; }
          .pp-compare { padding:0 16px 32px; }
          .pp-footer-note { padding:16px 16px 32px; }
        }
      `}</style>

      <div className="pp-root">

        {/* Hero */}
        <div className="pp-hero">
          <div className="pp-hero-inner">
            <button className="pp-back-btn" onClick={onBack}>← Back</button>
            <span className="pp-hero-icon">{meta.icon}</span>
            <h1 className="pp-hero-title">{product.name}</h1>
            <p className="pp-hero-desc">{product.description}</p>
          </div>
        </div>

        {/* Cycle toggle */}
        {!loading && availableIntervals.length > 1 && (
          <div className="pp-controls">
            <div className="pp-cycle-toggle">
              {availableIntervals.map(c => (
                <button key={c} className={`pp-cycle-btn${cycle === c ? " active" : ""}`} onClick={() => setCycle(c)}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                  {c === "yearly"   && <span className="pp-save-badge">Save 20%</span>}
                  {c === "lifetime" && <span className="pp-save-badge">One-time</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Plan cards */}
        {loading ? (
          <div className="pp-loading"><div className="pp-spinner" /> Loading plans…</div>
        ) : visiblePlans.length === 0 ? (
          <div className="pp-empty">
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <p>No {cycle} plans available yet.</p>
            {availableIntervals.length > 1 && <p style={{ marginTop: 6 }}>Try switching the billing period above.</p>}
          </div>
        ) : (
          <div className="pp-cards-wrap">
            <div className="pp-cards">
              {visiblePlans.map((plan, i) => {
                const price      = getPlanPrice(plan);
                const fmt        = price ? formatPrice(price, cycle) : null;
                const free       = price ? isPlanFree(price) : true;
                const isEnt      = plan.name.toLowerCase().includes("enterprise");
                const isPopular  = i === popularIdx;
                const isLifetime = plan.billingInterval === "lifetime";
                const features   = (plan.features?.$values ?? []).filter(f => f.displayLabel).slice(0, 4);

                return (
                  <div key={plan.id} className={`pp-plan-card${isPopular ? " popular" : ""}`}>
                    {isPopular && <div className="pp-popular-badge">Most popular</div>}

                    <div className="pp-plan-name">{plan.name}</div>

                    {fmt ? (
                      <div className="pp-price-block">
                        <div className="pp-plan-price-main">{fmt.main}</div>
                        <div className="pp-plan-price-period">{fmt.period}</div>
                        {fmt.note       && <div className="pp-plan-price-note">{fmt.note}</div>}
                        {fmt.modelLabel && <div className="pp-plan-price-model">{fmt.modelLabel}</div>}
                      </div>
                    ) : (
                      <div className="pp-no-price">Pricing on request</div>
                    )}

                    {plan.trialDays > 0 && (
                      <div className="pp-trial-badge">✦ {plan.trialDays}-day free trial</div>
                    )}

                    {plan.description && <p className="pp-plan-desc">{plan.description}</p>}

                    {features.length > 0 && (
                      <div className="pp-plan-features-preview">
                        {features.map(f => (
                          <div key={f.featureId} className="pp-feat-row">
                            <span className="pp-feat-check">✓</span>
                            <span>{f.displayLabel}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* CTA — always pinned to bottom, always 42px tall */}
                    <div className="pp-cta-wrap">
                      {isEnt ? (
                        <button className="pp-btn pp-btn-ghost">
                          Contact sales
                        </button>
                      ) : free ? (
                        <button className="pp-btn pp-btn-primary" onClick={() => price && onSubscribe?.(plan, price)}>
                          Get started — it's free
                        </button>
                      ) : (
                        <>
                          <button className="pp-btn pp-btn-primary" onClick={() => price && onSubscribe?.(plan, price)}>
                            {isLifetime ? "Buy now" : "Subscribe"}
                          </button>
                          {plan.trialDays > 0 && (
                            <button className="pp-btn pp-btn-outline" onClick={() => price && onSubscribe?.(plan, price)}>
                              Start {plan.trialDays}-day trial
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Feature comparison table */}
        {!loading && visiblePlans.length > 0 && featureRows.length > 0 && (
          <div className="pp-compare">
            <h2 className="pp-compare-title">Compare plans</h2>
            <table className="pp-table">
              <thead>
                <tr>
                  <th style={{ width: "28%" }}>Feature</th>
                  {visiblePlans.map((plan, i) => (
                    <th key={plan.id} className={`plan-col${i === popularIdx ? " popular-col" : ""}`}>{plan.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureRows.map(({ key, name }) => (
                  <tr key={key}>
                    <td style={{ fontWeight: 500 }}>{name}</td>
                    {visiblePlans.map((plan, i) => {
                      const feat = getPlanFeature(plan, key);
                      return (
                        <td key={plan.id} className="plan-val" style={i === popularIdx ? { background: `${meta.color}08` } : {}}>
                          {feat ? featureDisplay(feat.featureValue, feat.displayLabel, feat.valueType) : <span style={{ color: "#d1d5db" }}>—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* Price row */}
                <tr style={{ background: "#f8fafc" }}>
                  <td style={{ fontWeight: 700, color: "#0f172a" }}>Price</td>
                  {visiblePlans.map((plan, i) => {
                    const price = getPlanPrice(plan);
                    const fmt   = price ? formatPrice(price, cycle) : null;
                    const free  = price ? isPlanFree(price) : true;
                    const isEnt = plan.name.toLowerCase().includes("enterprise");
                    return (
                      <td key={plan.id} className="plan-val" style={i === popularIdx ? { background: `${meta.color}0a` } : {}}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>{fmt ? fmt.main : "—"}</div>
                        {fmt && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{fmt.period}</div>}
                        <div style={{ marginTop: 10 }}>
                          {isEnt ? (
                            <button className="pp-tbl-btn pp-tbl-btn-outline">Contact us</button>
                          ) : (
                            <button className="pp-tbl-btn pp-tbl-btn-filled" onClick={() => price && onSubscribe?.(plan, price)}>
                              {free ? "Get started" : "Subscribe"}
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="pp-footer-note">
            <p>All prices in USD · Cancel anytime · No hidden fees</p>
            <button className="pp-contact-link">Need a custom plan? Talk to sales →</button>
          </div>
        )}
      </div>
    </>
  );
};

export default PricingPage;